const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
/**
 * Find the next working day with available time slots
 * @param scheduleDate - The date to start checking from
 * @param openingHours - The opening hours of the store
 * @param defaultEta - Default ETA in minutes (optional, default: 0)
 * @param minimumEtaMinutes - Minimum ETA buffer in minutes (optional, default: 0)
 * @returns The next working day with available time
 */
const findNextWorkingDay = (
  scheduleDate: Date,
  openingHours: any,
  minimumEtaMinutes: number = 0,
  scheduleBufferTime: number = 0
): Date => {
  // Create a copy of the date to avoid modifying the original
  const currentDate = new Date();
  let nextDate = new Date(scheduleDate.getTime());
  let daysChecked = 0;

  if (!openingHours) {
    return scheduleDate;
  }
  // Check up to 7 days to avoid infinite loop
  while (daysChecked < 7) {
    const dayName = dayNames[nextDate.getDay()];
    const workingHours = openingHours[dayName];
    const isToday = nextDate.toDateString() === currentDate.toDateString();

    // If we find a day with working hours, check for available slots
    if (workingHours && workingHours.length > 0) {
      if (isToday) {
        // For today, check if there are available slots remaining
        const minimumTime = new Date(currentDate.getTime());

        // Check each working hour session for availability
        for (const session of workingHours) {
          const [fromHour, fromMinute] = session.from.split(":").map(Number);
          const [toHour, toMinute] = session.to.split(":").map(Number);

          let slotStart = new Date(nextDate);
          slotStart.setHours(fromHour, fromMinute, 0, 0);

          const slotEnd = new Date(nextDate);
          slotEnd.setHours(toHour, toMinute, 0, 0);

          // Check if there's still time available in this slot
          if (minimumTime < slotEnd) {
            const actualStartTime =
              minimumTime < slotStart
                ? slotStart
                : roundUpTime(minimumTime, 30);

            if (actualStartTime < slotEnd) {
              // Found an available slot today - add buffer time to the scheduled time
              const bufferTime = minimumEtaMinutes;
              const scheduledTime = new Date(
                actualStartTime.getTime() + bufferTime * 60 * 1000
              );

              // Check if scheduled time crosses midnight (next day)
              if (scheduledTime.getDate() !== nextDate.getDate()) {
                // Scheduled time is on the next day, update the date
                nextDate.setDate(nextDate.getDate() + 1);
              }

              nextDate.setHours(
                scheduledTime.getHours(),
                scheduledTime.getMinutes(),
                0,
                0
              );
              return nextDate;
            }
          }
        }
        // If no slots available today, continue to next day
      } else {
        // For future days, set time to the opening time of that day + buffer
        const [openingHour, openingMinute] = workingHours[0].from
          .split(":")
          .map(Number);

        let futureSlotStart = new Date(nextDate);
        futureSlotStart.setHours(openingHour, openingMinute, 0, 0);

        // Add buffer time for future days as well
        const bufferTime = minimumEtaMinutes + scheduleBufferTime;
        const scheduledTime = new Date(
          futureSlotStart.getTime() + bufferTime * 60 * 1000
        );

        // Check if scheduled time crosses midnight (next day)
        if (scheduledTime.getDate() !== nextDate.getDate()) {
          // Scheduled time is on the next day, update the date
          nextDate.setDate(nextDate.getDate() + 1);
        }

        nextDate.setHours(
          scheduledTime.getHours(),
          scheduledTime.getMinutes(),
          0,
          0
        );
        return nextDate;
      }
    }

    // Move to next day
    nextDate.setDate(nextDate.getDate() + 1);
    daysChecked++;
  }

  // If no working day found in next 7 days, return the original date
  return scheduleDate;
};

// Get the working hours for a specific day
const workingHoursForDay = (day: number, openingHours: any) => {
  return openingHours[dayNames[day]];
};

// Round up time to nearest 30 minutes
const roundUpTime = (timeStamp: Date, roundUpTime: 30 | 60): Date => {
  const timeToReturn = new Date(timeStamp);
  timeToReturn.setMilliseconds(0);
  timeToReturn.setSeconds(0);

  if (roundUpTime === 60) {
    timeToReturn.setMinutes(Math.ceil(timeToReturn.getMinutes() / 60) * 60);
  } else {
    timeToReturn.setMinutes(Math.ceil(timeToReturn.getMinutes() / 30) * 30);
  }

  return timeToReturn;
};

// Get available time slots for selected date
/**
 * Get available time slots for a given date
 * @param date - The date to get the available time slots for
 * @param openingHours - The opening hours of the store
 * @param defaultEta - The default ETA
 * @param minimumEtaMinutes - The minimum ETA minutes
 * @returns The available time slots
 */
const getAvailableTimeSlots = (
  date: Date,
  openingHours: any,
  defaultEta: number,
  minimumEtaMinutes: number
) => {
  const workingHours = workingHoursForDay(date.getDay(), openingHours);
  const currentTime = new Date();
  const isToday = date.toDateString() === currentTime.toDateString();

  const timeSlots: Array<{ value: string; label: string }> = [];

  workingHours.forEach((session: { from: string; to: string }) => {
    const [fromHour, fromMinute] = session.from.split(":").map(Number);
    const [toHour, toMinute] = session.to.split(":").map(Number);

    let startTime = new Date(date);
    startTime.setHours(fromHour, fromMinute);
    const bufferedStartTime = new Date(
      startTime.getTime() + Number(minimumEtaMinutes) * 60 * 1000
    );

    const endTime = new Date(date);
    endTime.setHours(toHour, toMinute);

    // If it's today, don't show past times
    if (isToday) {
      const minimumTime = new Date(currentTime);
      minimumTime.setMinutes(
        minimumTime.getMinutes() +
          parseInt(defaultEta.toString()) +
          minimumEtaMinutes
      );
      if (startTime < minimumTime) {
        startTime = roundUpTime(minimumTime, 30);
      }
    }

    // Generate 30-minute slots
    while (startTime < endTime) {
      if (startTime < bufferedStartTime) {
        // If the start time is before the buffered start time, skip the slot
        startTime.setMinutes(startTime.getMinutes() + 30);
        continue;
      }
      const timeString = startTime.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      timeSlots.push({
        value: timeString,
        label: timeString,
      });

      startTime.setMinutes(startTime.getMinutes() + 30);
    }
  });

  return timeSlots;
};

/**
 * Find the next schedule for a given day
 * @param startDay - The day to start checking from
 * @param schedule - The schedule to check
 * @returns The next schedule day or null if no schedule is found
 */
const findNextSchedule = (startDay: number, schedule: any[]) => {
  // Flatten the scheduleDays using reduce to gather all available days (for compatibility with older JS targets)
  const scheduleDays = schedule.reduce((acc: any[], item: any) => {
    if (item && item.days && Array.isArray(item.days.days)) {
      return acc.concat(item.days.days);
    }
    return acc;
  }, []);

  // Convert scheduleDays to a Set for faster lookups
  const scheduleDaysSet = new Set(
    scheduleDays.map((day: any) => day.toString())
  );

  // Create the days array starting from startDay
  const days = Array.from(
    { length: 7 },
    (_, index) => ((startDay + index - 1) % 7) + 1
  );

  // Loop through the week, checking if the current day is in the scheduleDaysSet
  for (const day of days) {
    if (scheduleDaysSet.has(day.toString())) {
      return day; // Return the found day number
    }
  }

  return null; // No schedule found for the next days
};

/**
 * Convert a time string to 12-hour format
 * @param time - The time string to convert
 * @returns The time string in 12-hour format
 */
const convertTo12Hours = (time: string) => {
  const [hours, minutes] = time.split(":");
  const hoursInt = parseInt(hours);
  const paddedMinutes = minutes.length === 1 ? `0${minutes}` : minutes;
  const hours12 = hoursInt % 12 || 12;
  const ampm = hoursInt >= 12 ? "PM" : "AM";
  return `${hours12}:${paddedMinutes} ${ampm}`;
};

/**
 * Find the day name from a given day number
 * @param dayNumber - The day number to find
 * @returns The day name
 */
const findDayFromDayNumber = (dayNumber: number) => {
  return dayNames[dayNumber - 1];
};

/**
 * Find the time slots for a given day
 * @param dayNumber - The day number to find
 * @param schedule - The schedule to check
 * @returns The time slots or null if no schedule is found
 */
const findTimeSlotsForDay = (dayNumber: number, schedule: any[]) => {
  const timeSlots: any[] = []; // Array to hold the time slots for the specified day
  for (const item of schedule) {
    // Check if the item has days and if the dayNumber is included
    if (
      item &&
      item.days &&
      Array.isArray(item.days.days) &&
      item.days.days.includes(dayNumber.toString())
    ) {
      // If the day matches, add the time slots to the array
      if (Array.isArray(item.time_slots)) {
        timeSlots.push(...item.time_slots); // Spread operator to add all time slots
      }
    }
  }

  return timeSlots; // Return the array of time slots found for the specified day
};

/**
 * Find the largest end time for a given day
 * @param dayNumber - The day number to find
 * @param schedule - The schedule to check
 * @returns The largest end time or null if no schedule is found
 */
const findLargestEndTime = (dayNumber: number, schedule: any[]) => {
  let largestEndTime: string | null = null;

  for (const item of schedule) {
    // Check if the item has days and if the dayNumber is included
    if (
      item &&
      item.days &&
      Array.isArray(item.days.days) &&
      item.days.days.includes(dayNumber.toString())
    ) {
      // Iterate through each time slot
      for (const timeSlot of item.time_slots) {
        if (timeSlot.end_time) {
          // Compare and find the largest end time
          if (!largestEndTime || timeSlot.end_time > largestEndTime) {
            largestEndTime = timeSlot.end_time; // Update largest end time
          }
        }
      }
    }
  }

  return largestEndTime; // Return the largest end time found, or null if none
};

/**
 * Check if the largest end time has passed
 * @param largestEndTime - The largest end time to check
 * @returns True if the largest end time has passed, false otherwise
 */
const checkLargestTimePassed = (largestEndTime: string) => {
  if (!largestEndTime) {
    return true; // If there's no end time, we can consider it as passed
  }

  // Get the current time
  const now = new Date();
  const [hours, minutes] = largestEndTime.split(":").map(Number);

  // Create a Date object for the largest end time today
  const endTimeToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes
  );

  // Compare the times
  return now > endTimeToday; // Returns true if the largest end time has passed
};

/**
 * Find the closest time for a given current time and next schedule times
 * @param currentTime - The current time to find the closest time for
 * @param nextScheduleTimes - The next schedule times to find the closest time for
 * @returns The closest time or null if no schedule is found
 */
const findClosestStartTime = (currentTime: string, timeSlots: any[]) => {
  const now = new Date();
  const [currentTimeHours, currentTimeMinutes] = currentTime
    .split(":")
    .map(Number);
  const currentTimeDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    currentTimeHours,
    currentTimeMinutes
  );

  // Extract start times from the time slots
  const startTimes = timeSlots.map((slot) => slot.start_time);

  // Map to Date objects
  const startTimeDates = startTimes.map((time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hours,
      minutes
    );
  });

  // If there are no valid start times, return null
  if (startTimeDates.length === 0) {
    return null; // or handle as needed
  }

  // Find the closest start time
  const closestStartTime = startTimeDates.reduce(
    (closest: Date, time: Date) => {
      const timeDiff = Math.abs(time.getTime() - currentTimeDate.getTime());
      const closestDiff = Math.abs(
        closest.getTime() - currentTimeDate.getTime()
      );
      return timeDiff < closestDiff ? time : closest;
    },
    startTimeDates[0]
  );

  // Return the closest start time as a string in "HH:MM" format
  const closestHours = closestStartTime.getHours().toString().padStart(2, "0");
  const closestMinutes = closestStartTime
    .getMinutes()
    .toString()
    .padStart(2, "0");
  return `${closestHours}:${closestMinutes}`;
};

const isItemAvailable = (
  schedule: any[],
  currentTime: string,
  defaultEta: number = 0,
  leadTime: number = 0
) => {
  // Input validation
  if (!schedule || !Array.isArray(schedule) || !currentTime) {
    return false;
  }

  // Get the current day number (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const currentDay = new Date().getDay() + 1;

  // Check if the schedule has entries for the current day
  const daySchedule = schedule.find(
    (item) =>
      item.days &&
      Array.isArray(item.days.days) &&
      item.days.days.includes(currentDay.toString())
  );

  // If no schedule is found for the current day, return false
  if (!daySchedule) {
    return false;
  }

  // Get the time slots for the current day
  const timeSlots = daySchedule.time_slots;

  // If no time slots are found, return false
  if (!timeSlots || timeSlots.length === 0) {
    return false;
  }

  const bufferTimeInMinutes = defaultEta + leadTime;

  // Convert the provided time to a Date object for comparison
  // Handle time formats like "09:13:06" or "09:13"
  const timeParts = currentTime.split(":");
  if (timeParts.length < 2) {
    return false;
  }

  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return false;
  }

  const providedTime = new Date();
  providedTime.setHours(hours, minutes, 0, 0); // Set seconds and milliseconds to 0

  // Calculate the new start time as a Date object
  const newStartTime = new Date(
    providedTime.getTime() + bufferTimeInMinutes * 60 * 1000
  );

  // Check if the new start time falls within any of the time slots
  for (const slot of timeSlots) {
    if (!slot.start_time || !slot.end_time) {
      continue;
    }

    // Parse start time
    const startTimeParts = slot.start_time.split(":");
    if (startTimeParts.length < 2) continue;

    const slotHours = parseInt(startTimeParts[0], 10);
    const slotMinutes = parseInt(startTimeParts[1], 10);

    if (isNaN(slotHours) || isNaN(slotMinutes)) continue;

    const slotStartTime = new Date();
    slotStartTime.setHours(slotHours, slotMinutes, 0, 0);

    // Parse end time - FIX: Use actual end_time instead of hardcoded 30 minutes
    const endTimeParts = slot.end_time.split(":");
    if (endTimeParts.length < 2) continue;

    const slotEndHours = parseInt(endTimeParts[0], 10);
    const slotEndMinutes = parseInt(endTimeParts[1], 10);

    if (isNaN(slotEndHours) || isNaN(slotEndMinutes)) continue;

    const slotEndTime = new Date();
    slotEndTime.setHours(slotEndHours, slotEndMinutes, 0, 0);

    // Check if the new start time is within the slot
    if (newStartTime >= slotStartTime && newStartTime <= slotEndTime) {
      return true;
    }
  }

  return false;
};

/**
 * Find the scheduled time
 * @param bufferedExpectedDeliveryTime - The buffered expected delivery time
 * @param bufferTime - The buffer time in minutes
 * @param defaultEta - The default ETA in minutes
 * @returns The scheduled time
 */
const findScheduledTime = (
  bufferedExpectedDeliveryTime: Date,
  bufferTime: number = 0,
  defaultEta: number = 0
) => {
  // Calculate total time to subtract (both bufferTime and defaultEta are in minutes)
  const totalMinutesToSubtract = bufferTime + defaultEta;

  // Convert minutes to milliseconds and subtract from the original date
  const adjustedTime = new Date(
    bufferedExpectedDeliveryTime.getTime() - totalMinutesToSubtract * 60 * 1000
  );

  // Return the result in ISO string format
  return adjustedTime.toISOString();
};

export {
  findNextWorkingDay,
  workingHoursForDay,
  roundUpTime,
  getAvailableTimeSlots,
  convertTo12Hours,
  findNextSchedule,
  findDayFromDayNumber,
  findTimeSlotsForDay,
  findLargestEndTime,
  checkLargestTimePassed,
  findClosestStartTime,
  isItemAvailable,
  findScheduledTime,
};
